import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  runTransaction,
  onSnapshot,
  limit
} from 'firebase/firestore';

/**
 * New Data Structure with User Isolation:
 * users/{userId}/
 *   ├── profile (document)
 *   │    ├── displayName: string
 *   │    ├── email: string
 *   │    ├── avatar: string (URL to storage)
 *   │    ├── preferences: object
 *   │    ├── createdAt: timestamp
 *   │    ├── lastLogin: timestamp
 *   │    ├── storageUsed: number
 *   │    └── plan: string
 *   ├── projects (collection)
 *   │   └── {projectId}
 *   │       ├── name: string
 *   │       ├── description: string
 *   │       ├── createdAt: timestamp
 *   │       ├── updatedAt: timestamp
 *   │       └── lastConversationId: string
 *   └── conversations (collection)
 *       └── {conversationId}
 *           ├── projectId: string
 *           ├── title: string
 *           ├── folderId: string | null
 *           ├── tags: string[]
 *           ├── priority: string
 *           ├── status: string
 *           ├── metadata: object
 *           ├── createdAt: timestamp
 *           ├── updatedAt: timestamp
 *           └── lastMessageId: string
 *
 * users/{userId}/folders (collection)
 *   └── {folderId}
 *       ├── name: string
 *       ├── description: string
 *       ├── color: string
 *       ├── icon: string
 *       ├── parentFolderId: string | null
 *       ├── conversationIds: string[]
 *       ├── createdAt: timestamp
 *       └── updatedAt: timestamp
 *
 * Legacy Structure (to be migrated):
 * - projects (collection) - will be moved to users/{userId}/projects
 * - conversations (collection) - will be moved to users/{userId}/conversations
 * - messages (collection) - will be moved to users/{userId}/conversations/{conversationId}/messages
 */

// Custom error class for Firestore operations
class FirestoreError extends Error {
  constructor(message, operation) {
    super(message);
    this.name = 'FirestoreError';
    this.operation = operation;
  }
}

// ==================== USER PROFILE FUNCTIONS ====================

/**
 * Create or update user profile
 * @param {string} userId - The user ID
 * @param {Object} profileData - The profile data
 * @returns {Promise<void>}
 */
const createOrUpdateUserProfile = async (userId, profileData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      // Update existing profile
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new profile
      await setDoc(userRef, {
        ...profileData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        storageUsed: 0,
        plan: 'free'
      });
    }
  } catch (error) {
    throw new FirestoreError(`Failed to create/update user profile: ${error.message}`, 'createOrUpdateUserProfile');
  }
};

/**
 * Get user profile
 * @param {string} userId - The user ID
 * @returns {Promise<Object|null>} The user profile or null if not found
 */
const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    // Handle offline errors gracefully
    if (error.code === 'unavailable' || error.message.includes('offline') || error.message.includes('network')) {
      console.warn('Firestore offline, returning null for user profile');
      return null;
    }
    throw new FirestoreError(`Failed to get user profile: ${error.message}`, 'getUserProfile');
  }
};

/**
 * Update user storage usage
 * @param {string} userId - The user ID
 * @param {number} bytes - Bytes to add/subtract
 * @returns {Promise<void>}
 */
const updateUserStorage = async (userId, bytes) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      storageUsed: bytes,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw new FirestoreError(`Failed to update user storage: ${error.message}`, 'updateUserStorage');
  }
};

// ==================== FOLDER FUNCTIONS ====================

/**
 * Create a new folder
 * @param {string} userId - The user ID
 * @param {Object} folderData - The folder data
 * @returns {Promise<string>} The ID of the created folder
 */
const createFolder = async (userId, folderData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const foldersRef = collection(userRef, 'folders');

    const newFolder = {
      ...folderData,
      conversationIds: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const folderDocRef = await addDoc(foldersRef, newFolder);
    return folderDocRef.id;
  } catch (error) {
    throw new FirestoreError(`Failed to create folder: ${error.message}`, 'createFolder');
  }
};

/**
 * Get all folders for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} An array of folder objects
 */
const getFolders = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const foldersRef = collection(userRef, 'folders');
    const q = query(foldersRef, orderBy('updatedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw new FirestoreError(`Failed to get folders: ${error.message}`, 'getFolders');
  }
};

/**
 * Update a folder
 * @param {string} userId - The user ID
 * @param {string} folderId - The folder ID
 * @param {Object} folderData - The updated folder data
 * @returns {Promise<void>}
 */
const updateFolder = async (userId, folderId, folderData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const folderRef = doc(userRef, 'folders', folderId);
    await updateDoc(folderRef, {
      ...folderData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw new FirestoreError(`Failed to update folder: ${error.message}`, 'updateFolder');
  }
};

/**
 * Delete a folder and move conversations to root
 * @param {string} userId - The user ID
 * @param {string} folderId - The folder ID
 * @returns {Promise<void>}
 */
const deleteFolder = async (userId, folderId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const folderRef = doc(userRef, 'folders', folderId);
    const folderDoc = await getDoc(folderRef);

    if (folderDoc.exists()) {
      const folderData = folderDoc.data();

      // Remove folderId from all conversations in this folder
      for (const conversationId of folderData.conversationIds || []) {
        const conversationRef = doc(userRef, 'conversations', conversationId);
        await updateDoc(conversationRef, {
          folderId: null,
          updatedAt: serverTimestamp()
        });
      }
    }

    await deleteDoc(folderRef);
  } catch (error) {
    throw new FirestoreError(`Failed to delete folder: ${error.message}`, 'deleteFolder');
  }
};

/**
 * Add conversation to folder
 * @param {string} userId - The user ID
 * @param {string} folderId - The folder ID
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<void>}
 */
const addConversationToFolder = async (userId, folderId, conversationId) => {
  try {
    const userRef = doc(db, 'users', userId);

    // Update conversation
    const conversationRef = doc(userRef, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      folderId: folderId,
      updatedAt: serverTimestamp()
    });

    // Update folder
    const folderRef = doc(userRef, 'folders', folderId);
    const folderDoc = await getDoc(folderRef);

    if (folderDoc.exists()) {
      const folderData = folderDoc.data();
      const conversationIds = folderData.conversationIds || [];

      if (!conversationIds.includes(conversationId)) {
        await updateDoc(folderRef, {
          conversationIds: [...conversationIds, conversationId],
          updatedAt: serverTimestamp()
        });
      }
    }
  } catch (error) {
    throw new FirestoreError(`Failed to add conversation to folder: ${error.message}`, 'addConversationToFolder');
  }
};

/**
 * Remove conversation from folder
 * @param {string} userId - The user ID
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<void>}
 */
const removeConversationFromFolder = async (userId, conversationId) => {
  try {
    const userRef = doc(db, 'users', userId);

    // Get conversation to find current folder
    const conversationRef = doc(userRef, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data();
      const folderId = conversationData.folderId;

      if (folderId) {
        // Remove conversation from folder
        const folderRef = doc(userRef, 'folders', folderId);
        const folderDoc = await getDoc(folderRef);

        if (folderDoc.exists()) {
          const folderData = folderDoc.data();
          const conversationIds = (folderData.conversationIds || []).filter(id => id !== conversationId);

          await updateDoc(folderRef, {
            conversationIds: conversationIds,
            updatedAt: serverTimestamp()
          });
        }

        // Remove folderId from conversation
        await updateDoc(conversationRef, {
          folderId: null,
          updatedAt: serverTimestamp()
        });
      }
    }
  } catch (error) {
    throw new FirestoreError(`Failed to remove conversation from folder: ${error.message}`, 'removeConversationFromFolder');
  }
};

// ==================== NEW PROJECT FUNCTIONS (with user isolation) ====================

/**
 * Create a new project for a user
 * @param {string} userId - The user ID
 * @param {Object} projectData - The project data (name, description)
 * @returns {Promise<string>} The ID of the created project
 */
const createProject = async (userId, projectData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const projectsRef = collection(userRef, 'projects');

    const newProject = {
      ...projectData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastConversationId: null
    };

    const projectDocRef = await addDoc(projectsRef, newProject);
    return projectDocRef.id;
  } catch (error) {
    throw new FirestoreError(`Failed to create project: ${error.message}`, 'createProject');
  }
};

/**
 * Get all projects for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} An array of project objects
 */
const getProjects = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const projectsRef = collection(userRef, 'projects');
    const q = query(projectsRef, orderBy('updatedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw new FirestoreError(`Failed to get projects: ${error.message}`, 'getProjects');
  }
};

/**
 * Update a project
 * @param {string} userId - The user ID
 * @param {string} projectId - The project ID
 * @param {Object} projectData - The updated project data
 * @returns {Promise<void>}
 */
const updateProject = async (userId, projectId, projectData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const projectRef = doc(userRef, 'projects', projectId);
    await updateDoc(projectRef, {
      ...projectData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw new FirestoreError(`Failed to update project: ${error.message}`, 'updateProject');
  }
};

/**
 * Delete a project and all its associated conversations and messages
 * @param {string} userId - The user ID
 * @param {string} projectId - The project ID
 * @returns {Promise<void>}
 */
const deleteProject = async (userId, projectId) => {
  try {
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', userId);

      // Get all conversations for this project
      const conversationsRef = collection(userRef, 'conversations');
      const conversationsQuery = query(conversationsRef, where('projectId', '==', projectId));
      const conversationsDocs = await getDocs(conversationsQuery);

      // Delete all messages in each conversation
      for (const conversationDoc of conversationsDocs.docs) {
        const messagesRef = collection(conversationDoc.ref, 'messages');
        const messagesDocs = await getDocs(messagesRef);
        messagesDocs.forEach(doc => {
          transaction.delete(doc.ref);
        });
        transaction.delete(conversationDoc.ref);
      }

      // Delete the project
      const projectRef = doc(userRef, 'projects', projectId);
      transaction.delete(projectRef);
    });
  } catch (error) {
    throw new FirestoreError(`Failed to delete project: ${error.message}`, 'deleteProject');
  }
};

// ==================== NEW CONVERSATION FUNCTIONS (with user isolation) ====================

/**
 * Create a new conversation for a user
 * @param {string} userId - The user ID
 * @param {Object} conversationData - The conversation data
 * @returns {Promise<string>} The ID of the created conversation
 */
const createConversation = async (userId, conversationData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const conversationsRef = collection(userRef, 'conversations');

    // Generate a unique ID for the conversation
    const conversationId = `conv_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const conversationDocRef = doc(conversationsRef, conversationId);

    const newConversation = {
      ...conversationData,
      folderId: conversationData.folderId || null,
      tags: conversationData.tags || [],
      priority: conversationData.priority || 'normal',
      status: conversationData.status || 'active',
      metadata: conversationData.metadata || {
        messageCount: 0,
        lastActivity: serverTimestamp(),
        attachments: { images: 0, videos: 0, documents: 0 }
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessageId: null
    };

    await setDoc(conversationDocRef, newConversation);
    return conversationId;
  } catch (error) {
    throw new FirestoreError(`Failed to create conversation: ${error.message}`, 'createConversation');
  }
};

/**
 * Get all conversations for a user
 * @param {string} userId - The user ID
 * @param {Object} filters - Optional filters (projectId, folderId, status, etc.)
 * @returns {Promise<Array>} An array of conversation objects
 */
const getConversations = async (userId, filters = {}) => {
  try {
    const userRef = doc(db, 'users', userId);
    const conversationsRef = collection(userRef, 'conversations');

    let q = conversationsRef;

    // Apply filters
    const conditions = [];

    if (filters.projectId) {
      conditions.push(where('projectId', '==', filters.projectId));
    }

    if (filters.folderId !== undefined) {
      conditions.push(where('folderId', '==', filters.folderId));
    }

    if (filters.status) {
      conditions.push(where('status', '==', filters.status));
    }

    if (filters.tags && filters.tags.length > 0) {
      conditions.push(where('tags', 'array-contains-any', filters.tags));
    }

    if (conditions.length > 0) {
      q = query(conversationsRef, ...conditions, orderBy('updatedAt', 'desc'));
    } else {
      q = query(conversationsRef, orderBy('updatedAt', 'desc'));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw new FirestoreError(`Failed to get conversations: ${error.message}`, 'getConversations');
  }
};

/**
 * Update a conversation
 * @param {string} userId - The user ID
 * @param {string} conversationId - The conversation ID
 * @param {Object} conversationData - The updated conversation data
 * @returns {Promise<void>}
 */
const updateConversation = async (userId, conversationId, conversationData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const conversationRef = doc(userRef, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      ...conversationData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw new FirestoreError(`Failed to update conversation: ${error.message}`, 'updateConversation');
  }
};

/**
 * Delete a conversation and all its messages
 * @param {string} userId - The user ID
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<void>}
 */
const deleteConversation = async (userId, conversationId) => {
  try {
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', userId);
      const conversationRef = doc(userRef, 'conversations', conversationId);

      // Get conversation to check folderId
      const conversationDoc = await getDoc(conversationRef);

      if (conversationDoc.exists()) {
        const conversationData = conversationDoc.data();

        // Remove from folder if in one
        if (conversationData.folderId) {
          const folderRef = doc(userRef, 'folders', conversationData.folderId);
          const folderDoc = await getDoc(folderRef);

          if (folderDoc.exists()) {
            const folderData = folderDoc.data();
            const conversationIds = (folderData.conversationIds || []).filter(id => id !== conversationId);

            transaction.update(folderRef, {
              conversationIds: conversationIds,
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      // Delete all messages
      const messagesRef = collection(conversationRef, 'messages');
      const messagesDocs = await getDocs(messagesRef);
      messagesDocs.forEach(doc => {
        transaction.delete(doc.ref);
      });

      // Delete conversation
      transaction.delete(conversationRef);
    });
  } catch (error) {
    throw new FirestoreError(`Failed to delete conversation: ${error.message}`, 'deleteConversation');
  }
};

// ==================== NEW MESSAGE FUNCTIONS (with user isolation) ====================

/**
 * Add a new message to a conversation
 * @param {string} userId - The user ID
 * @param {string} conversationId - The conversation ID
 * @param {Object} messageData - The message data (content, role, attachments, etc.)
 * @returns {Promise<string>} The ID of the created message
 */
const addMessage = async (userId, conversationId, messageData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const conversationRef = doc(userRef, 'conversations', conversationId);
    const messagesRef = collection(conversationRef, 'messages');

    const newMessage = {
      ...messageData,
      createdAt: serverTimestamp()
    };

    const messageDocRef = await addDoc(messagesRef, newMessage);

    // Update conversation metadata
    const conversationDoc = await getDoc(conversationRef);
    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data();
      const currentMessageCount = conversationData.metadata?.messageCount || 0;

      await updateDoc(conversationRef, {
        lastMessageId: messageDocRef.id,
        updatedAt: serverTimestamp(),
        'metadata.messageCount': currentMessageCount + 1,
        'metadata.lastActivity': serverTimestamp()
      });
    }

    return messageDocRef.id;
  } catch (error) {
    throw new FirestoreError(`Failed to add message: ${error.message}`, 'addMessage');
  }
};

/**
 * Get all messages for a conversation
 * @param {string} userId - The user ID
 * @param {string} conversationId - The conversation ID
 * @param {number} limit - Optional limit on number of messages
 * @returns {Promise<Array>} An array of message objects
 */
const getMessages = async (userId, conversationId, maxMessages = 50) => {
  try {
    const userRef = doc(db, 'users', userId);
    const conversationRef = doc(userRef, 'conversations', conversationId);
    const messagesRef = collection(conversationRef, 'messages');

    const q = query(
      messagesRef,
      orderBy('createdAt', 'desc'),
      ...(maxMessages ? [limit(maxMessages)] : [])
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).reverse();
  } catch (error) {
    throw new FirestoreError(`Failed to get messages: ${error.message}`, 'getMessages');
  }
};

/**
 * Update a message
 * @param {string} userId - The user ID
 * @param {string} conversationId - The conversation ID
 * @param {string} messageId - The message ID
 * @param {Object} messageData - The updated message data
 * @returns {Promise<void>}
 */
const updateMessage = async (userId, conversationId, messageId, messageData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const conversationRef = doc(userRef, 'conversations', conversationId);
    const messageRef = doc(conversationRef, 'messages', messageId);
    await updateDoc(messageRef, messageData);
  } catch (error) {
    throw new FirestoreError(`Failed to update message: ${error.message}`, 'updateMessage');
  }
};

/**
 * Delete a message
 * @param {string} userId - The user ID
 * @param {string} conversationId - The conversation ID
 * @param {string} messageId - The message ID
 * @returns {Promise<void>}
 */
const deleteMessage = async (userId, conversationId, messageId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const conversationRef = doc(userRef, 'conversations', conversationId);
    const messageRef = doc(conversationRef, 'messages', messageId);

    await deleteDoc(messageRef);

    // Update conversation metadata
    const conversationDoc = await getDoc(conversationRef);
    if (conversationDoc.exists()) {
      const conversationData = conversationDoc.data();
      const currentMessageCount = conversationData.metadata?.messageCount || 1;

      await updateDoc(conversationRef, {
        'metadata.messageCount': Math.max(0, currentMessageCount - 1),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    throw new FirestoreError(`Failed to delete message: ${error.message}`, 'deleteMessage');
  }
};

// Legacy Functions - will be removed after migration

/**
 * Create a new project (Legacy - without user isolation)
 * @param {Object} projectData - The project data (name, description)
 * @returns {Promise<string>} The ID of the created project
 */
const createProjectLegacy = async (projectData) => {
  try {
    const projectRef = doc(collection(db, 'projects'));
    const newProject = {
      ...projectData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastConversationId: null
    };
    await setDoc(projectRef, newProject);
    return projectRef.id;
  } catch (error) {
    console.error('Error creating project:', error);
    throw new FirestoreError(`Failed to create project: ${error.message}`, 'createProject');
  }
};

/**
 * Get all projects for a user (Legacy)
 * @param {string} userId - The ID of the user
 * @returns {Promise<Array>} An array of project objects
 */
const getProjectsLegacy = async (userId) => {
  try {
    const projectsRef = collection(db, 'projects');
    const q = query(projectsRef, where('userId', '==', userId), orderBy('updatedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw new FirestoreError(`Failed to get projects: ${error.message}`, 'getProjects');
  }
};

/**
 * Update a project (Legacy)
 * @param {string} projectId - The ID of the project to update
 * @param {Object} projectData - The updated project data
 * @returns {Promise<void>}
 */
const updateProjectLegacy = async (projectId, projectData) => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      ...projectData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw new FirestoreError(`Failed to update project: ${error.message}`, 'updateProject');
  }
};

/**
 * Delete a project and all its associated conversations and messages (Legacy)
 * @param {string} projectId - The ID of the project to delete
 * @returns {Promise<void>}
 */
const deleteProjectLegacy = async (projectId) => {
  try {
    await runTransaction(db, async (transaction) => {
      const messagesQuery = query(collection(db, 'messages'), where('projectId', '==', projectId));
      const messagesDocs = await getDocs(messagesQuery);
      messagesDocs.forEach(doc => {
        transaction.delete(doc.ref);
      });

      const conversationsQuery = query(collection(db, 'conversations'), where('projectId', '==', projectId));
      const conversationsDocs = await getDocs(conversationsQuery);
      conversationsDocs.forEach(doc => {
        transaction.delete(doc.ref);
      });

      const projectRef = doc(db, 'projects', projectId);
      transaction.delete(projectRef);
    });
  } catch (error) {
    throw new FirestoreError(`Failed to delete project: ${error.message}`, 'deleteProject');
  }
};

// Conversations (Legacy)

/**
 * Create a new conversation within a project (Legacy)
 * @param {string} projectId - The ID of the project
 * @param {Object} initialMessage - The initial message for the conversation
 * @returns {Promise<string>} The ID of the created conversation
 */
const createNewConversationLegacy = async (projectId, initialMessage) => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    const conversationsRef = collection(projectRef, 'conversations');
    
    const newConversation = {
      title: 'New Conversation',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessageId: null
    };
    
    const conversationDocRef = await addDoc(conversationsRef, newConversation);
    
    if (initialMessage) {
      const messagesRef = collection(conversationDocRef, 'messages');
      const messageDocRef = await addDoc(messagesRef, {
        ...initialMessage,
        createdAt: serverTimestamp()
      });
      
      await updateDoc(conversationDocRef, {
        lastMessageId: messageDocRef.id,
        updatedAt: serverTimestamp()
      });
    }
    
    await updateDoc(projectRef, {
      lastConversationId: conversationDocRef.id,
      updatedAt: serverTimestamp()
    });
    
    return conversationDocRef.id;
  } catch (error) {
    throw new FirestoreError(`Failed to create new conversation: ${error.message}`, 'createNewConversation');
  }
};

/**
 * Get all conversations for a project (Legacy)
 * @param {string} projectId - The ID of the project
 * @returns {Promise<Array>} An array of conversation objects
 */
const getConversationsLegacy = async (projectId) => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    const conversationsRef = collection(projectRef, 'conversations');
    const q = query(conversationsRef, orderBy('updatedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw new FirestoreError(`Failed to get conversations: ${error.message}`, 'getConversations');
  }
};

/**
 * Update a conversation (Legacy)
 * @param {string} conversationId - The ID of the conversation to update
 * @param {Object} conversationData - The updated conversation data
 * @returns {Promise<void>}
 */
const updateConversationLegacy = async (conversationId, conversationData) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    await updateDoc(conversationRef, {
      ...conversationData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw new FirestoreError(`Failed to update conversation: ${error.message}`, 'updateConversation');
  }
};

/**
 * Delete a conversation and all its messages (Legacy)
 * @param {string} conversationId - The ID of the conversation to delete
 * @returns {Promise<void>}
 */
const deleteConversationLegacy = async (conversationId) => {
  try {
    await runTransaction(db, async (transaction) => {
      const messagesQuery = query(collection(db, 'messages'), where('conversationId', '==', conversationId));
      const messagesDocs = await getDocs(messagesQuery);
      messagesDocs.forEach(doc => {
        transaction.delete(doc.ref);
      });

      const conversationRef = doc(db, 'conversations', conversationId);
      transaction.delete(conversationRef);
    });
  } catch (error) {
    throw new FirestoreError(`Failed to delete conversation: ${error.message}`, 'deleteConversation');
  }
};

// Messages (Legacy)

/**
 * Add a new message to a conversation (Legacy)
 * @param {string} projectId - The ID of the project
 * @param {string} conversationId - The ID of the conversation
 * @param {Object} messageData - The message data (content, role)
 * @returns {Promise<string>} The ID of the created message
 */
const addMessageLegacy = async (projectId, conversationId, messageData) => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    const conversationRef = doc(projectRef, 'conversations', conversationId);
    const messagesRef = collection(conversationRef, 'messages');
    
    const newMessage = {
      ...messageData,
      createdAt: serverTimestamp()
    };
    
    const messageDocRef = await addDoc(messagesRef, newMessage);
    
    await updateDoc(conversationRef, {
      lastMessageId: messageDocRef.id,
      updatedAt: serverTimestamp()
    });
    
    return messageDocRef.id;
  } catch (error) {
    throw new FirestoreError(`Failed to add message: ${error.message}`, 'addMessage');
  }
};

/**
 * Get all messages for a conversation (Legacy)
 * @param {string} conversationId - The ID of the conversation
 * @returns {Promise<Array>} An array of message objects
 */
const getMessagesLegacy = async (conversationId) => {
  try {
    const messagesRef = collection(db, 'messages');
    const q = query(messagesRef, where('conversationId', '==', conversationId), orderBy('createdAt'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw new FirestoreError(`Failed to get messages: ${error.message}`, 'getMessages');
  }
};

/**
 * Update a message (Legacy)
 * @param {string} messageId - The ID of the message to update
 * @param {Object} messageData - The updated message data
 * @returns {Promise<void>}
 */
const updateMessageLegacy = async (messageId, messageData) => {
  try {
    const messageRef = doc(db, 'messages', messageId);
    await updateDoc(messageRef, messageData);
  } catch (error) {
    throw new FirestoreError(`Failed to update message: ${error.message}`, 'updateMessage');
  }
};

/**
 * Delete a message (Legacy)
 * @param {string} messageId - The ID of the message to delete
 * @returns {Promise<void>}
 */
const deleteMessageLegacy = async (messageId) => {
  try {
    const messageRef = doc(db, 'messages', messageId);
    await deleteDoc(messageRef);
  } catch (error) {
    throw new FirestoreError(`Failed to delete message: ${error.message}`, 'deleteMessage');
  }
};

// Real-time listeners

/**
 * Set up a real-time listener for projects
 * @param {function} callback - Function to call with updated projects data
 * @returns {function} Unsubscribe function
 */
const listenToProjects = (callback) => {
  const projectsRef = collection(db, 'projects');
  const q = query(projectsRef, orderBy('updatedAt', 'desc'));
  
  return onSnapshot(q, 
    (snapshot) => {
      const projects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(null, projects);
    },
    (error) => {
      console.error("Error listening to projects:", error);
      callback(new FirestoreError(`Failed to listen to projects: ${error.message}`, 'listenToProjects'), null);
    }
  );
};

/**
 * Set up a real-time listener for a project's conversations
 * @param {string} projectId - The ID of the project
 * @param {function} callback - Function to call with updated conversations data
 * @returns {function} Unsubscribe function
 */
const listenToConversations = (projectId, callback) => {
  const conversationsRef = collection(db, 'conversations');
  const q = query(conversationsRef, where('projectId', '==', projectId), orderBy('updatedAt', 'desc'));
  
  return onSnapshot(q, 
    (snapshot) => {
      const conversations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(null, conversations);
    },
    (error) => {
      console.error("Error listening to conversations:", error);
      callback(new FirestoreError(`Failed to listen to conversations: ${error.message}`, 'listenToConversations'), null);
    }
  );
};

/**
 * Set up a real-time listener for a conversation's messages
 * @param {string} conversationId - The ID of the conversation
 * @param {function} callback - Function to call with updated messages data
 * @param {number} [messageLimit=50] - Optional limit on the number of messages to retrieve
 * @returns {function} Unsubscribe function
 */
const listenToMessages = (conversationId, callback, messageLimit = 50) => {
  const messagesRef = collection(db, 'messages');
  const q = query(
    messagesRef, 
    where('conversationId', '==', conversationId), 
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );
  
  return onSnapshot(q, 
    (snapshot) => {
      const messages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                                    .reverse(); // Reverse to get chronological order
      callback(null, messages);
    },
    (error) => {
      console.error("Error listening to messages:", error);
      callback(new FirestoreError(`Failed to listen to messages: ${error.message}`, 'listenToMessages'), null);
    }
  );
};

// ==================== NEW EXPORTS (with user isolation) ====================
export {
  // User Profile Functions
  createOrUpdateUserProfile,
  getUserProfile,
  updateUserStorage,

  // Folder Functions
  createFolder,
  getFolders,
  updateFolder,
  deleteFolder,
  addConversationToFolder,
  removeConversationFromFolder,

  // New Functions (with user isolation)
  createProject,
  getProjects,
  updateProject,
  deleteProject,
  createConversation,
  getConversations,
  updateConversation,
  deleteConversation,
  addMessage,
  getMessages,
  updateMessage,
  deleteMessage,

  // Legacy Functions (to be deprecated - kept for backward compatibility)
  // These will be removed after migration is complete
  createProjectLegacy,
  getProjectsLegacy,
  updateProjectLegacy,
  deleteProjectLegacy,
  createNewConversationLegacy,
  getConversationsLegacy,
  updateConversationLegacy,
  deleteConversationLegacy,
  addMessageLegacy,
  getMessagesLegacy,
  updateMessageLegacy,
  deleteMessageLegacy,

  // Real-time listeners (legacy - will be updated)
  listenToProjects,
  listenToConversations,
  listenToMessages
};